/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { DragEvent, useCallback, useMemo } from "react";

import { ViewModel } from "../../Common/ViewModel";
import { ComponentPalette, IComponentPaletteItem } from "../../../../ui/components/ComponentPalette";
import { ComponentCategories } from "../../../../common/ComponentCategories";
import { Panel } from "../../../../ui/components/Panel";
import { dndSetDataTransferFromNew, INewItem, useTranslate } from "@adapptio/lib-components";

/**
 * Component props
 */
export interface IViewEditorComponentPaletteProps {
	model: ViewModel;
}

/**
 * Main Application Component
 *
 * @param props Component props
 */
export const ViewEditorComponentPalette: React.FunctionComponent<IViewEditorComponentPaletteProps> = ({
	model
}) => {
	const t = useTranslate();

	const components = useMemo(() => {
		return model.getComponentList().map((cmpDef) => {
			return {
				name: cmpDef.name,
				category: cmpDef.category,
				label: t("editor", cmpDef.label),
				description: t("editor", cmpDef.description),
				icon: cmpDef.icon,
				docUrl: cmpDef.docUrl,
				order: cmpDef.order,
				hidden: cmpDef.hidden
			} as IComponentPaletteItem;
		});
	}, [ model ]);

	const onComponentDragStart = useCallback(
		(item: IComponentPaletteItem, ev: DragEvent<HTMLLIElement>) => {
			const cmpDef = model.getComponentDefinition(item.name);

			const width = cmpDef.initialWidth || null;
			const height = cmpDef.initialHeight || null;

			const items = [
				{
					blueprint: {
						component: cmpDef.name,
						id: cmpDef.initialVariableName ?? cmpDef.name
					},
					dimensions: {
						width: width,
						height: height,
						parentwidth: width,
						parentheight: height
					},
					offset: {
						x: width / 2 || 0,
						y: height / 2 || 0,
						parentx: width / 2 || 0,
						parenty: height / 2 || 0
					}
				} as INewItem
			];

			ev.stopPropagation();
			ev.nativeEvent.dataTransfer.effectAllowed = "move";
			dndSetDataTransferFromNew(ev.nativeEvent.dataTransfer, items);

			// const ghostImage = new Image();
			// ghostImage.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';
			// ev.nativeEvent.dataTransfer.setDragImage(ghostImage, 0, 0);
		},
		[ model ]
	);

	return (
		<Panel scroll={true}>
			<ComponentPalette
				components={components}
				categories={ComponentCategories}
				onDragStart={onComponentDragStart}
			/>
		</Panel>
	);
};
